import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API_URL } from '../../Constants';
import { AuthContext } from '../context/AuthContext';

const CreateWhatsappTemplate = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { user } = useContext(AuthContext);
  const token = user?.token || localStorage.getItem("token");
  const companyId =
    user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  // Form state for creating/editing templates
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    language: 'en_US',
    header: {
      type: 'TEXT',
      text: ''
    },
    body: {
      text: '',
      examples: ['']
    },
    footer: {
      text: ''
    },
    buttons: [{ type: 'QUICK_REPLY', text: '', payload: '' }],
  });

  // Helper function to transform API data to our structure
  const transformTemplateData = (apiData) => {
    return apiData.map(template => {
      return {
        id: template.id,
        name: template.name,
        category: template.category.toLowerCase(),
        language: template.language,
        header: template.headerText || '', // Use headerText instead of header?.text
        body: template.body || '', // Use body directly, not body?.text
        footer: template.footer || '', // Use footer directly
        buttons: template.buttons || [],
        status: template.status?.toLowerCase() || 'pending', // Also fixed status to lowercase
        active: template.status === 'APPROVED' // Set active based on status
      };
    });
  };

  // Sync templates with marketing platform
  const syncTemplates = async (templateName) => {
    try {
      await axios.get(
        `${API_URL}/api/templates/${companyId}/sync/${templateName}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Templates synced successfully!");
      return true;
    } catch (err) {
      console.error("Error syncing templates:", err);
      setError("Failed to sync templates. Please try again.");
      return false;
    }
  };

  // API call to fetch templates (GET)
  const fetchTemplates = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/templates/getByCompanyId/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const transformedTemplates = transformTemplateData(response.data);
      setTemplates(transformedTemplates);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setError("Failed to fetch templates. Please try again.");
    }
  };

  // Initialize templates - first sync, then fetch
  const initializeTemplates = async () => {
    try {
      setLoading(true);
      // First call sync API
      const syncSuccess = await syncTemplates('marketing_template');
      
      // Only fetch templates if sync was successful
      if (syncSuccess) {
        await fetchTemplates();
      }
    } catch (err) {
      console.error("Error initializing templates:", err);
      setError("Failed to initialize templates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch templates on component mount
  useEffect(() => {
    initializeTemplates();
  }, []);

  // View template details
  const viewTemplateDetails = (template) => {
    setSelectedTemplate(template);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle header text change
  const handleHeaderChange = (value) => {
    setFormData({
      ...formData,
      header: {
        ...formData.header,
        text: value
      }
    });
  };

  // Handle body text change
  const handleBodyChange = (value) => {
    setFormData({
      ...formData,
      body: {
        ...formData.body,
        text: value
      }
    });
    
    // Extract variables from body text and update examples
    const variables = value.match(/\{\{\d+\}\}/g) || [];
    const currentExamples = [...formData.body.examples];
    
    // Ensure examples array matches the number of variables
    while (currentExamples.length < variables.length) {
      currentExamples.push('');
    }
    while (currentExamples.length > variables.length) {
      currentExamples.pop();
    }
    
    setFormData(prev => ({
      ...prev,
      body: {
        ...prev.body,
        examples: currentExamples
      }
    }));
  };

  // Handle example input change for variables
  const handleExampleChange = (index, value) => {
    const updatedExamples = [...formData.body.examples];
    updatedExamples[index] = value;
    setFormData({
      ...formData,
      body: {
        ...formData.body,
        examples: updatedExamples
      }
    });
  };

  // Handle footer text change
  const handleFooterChange = (value) => {
    setFormData({
      ...formData,
      footer: {
        ...formData.footer,
        text: value
      }
    });
  };

  // Handle button changes
  const handleButtonChange = (index, field, value) => {
    const updatedButtons = [...formData.buttons];
    updatedButtons[index][field] = value;
    
    // Fix field names to match API response
    if (field === 'phone_number') {
      updatedButtons[index]['phoneNumber'] = value;
    } else if (field === 'url') {
      updatedButtons[index]['url'] = value;
    } else if (field === 'payload') {
      updatedButtons[index]['payload'] = value;
    }
    
    setFormData({
      ...formData,
      buttons: updatedButtons
    });
  };

  // Add a new button
  const addButton = () => {
    setFormData({
      ...formData,
      buttons: [...formData.buttons, { type: 'QUICK_REPLY', text: '', payload: '' }]
    });
  };

  // Remove a button
  const removeButton = (index) => {
    const updatedButtons = formData.buttons.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      buttons: updatedButtons
    });
  };

  // Add variable to body text
  const addVariableToBody = () => {
    const variables = formData.body.text.match(/\{\{\d+\}\}/g) || [];
    const nextVariableNumber = variables.length + 1;
    const newText = formData.body.text + ` {{${nextVariableNumber}}}`;
    handleBodyChange(newText);
  };

  // API call to create template (POST)
  const createTemplate = async () => {
    // Prepare buttons data with correct field names
    const preparedButtons = formData.buttons.filter(button => button.text).map(button => {
      const baseButton = {
        type: button.type,
        text: button.text
      };
      
      // Add appropriate fields based on button type
      switch (button.type) {
        case 'QUICK_REPLY':
          return { ...baseButton, payload: button.payload || '' };
        case 'URL':
          return { ...baseButton, url: button.url || '' };
        case 'PHONE_NUMBER':
          return { ...baseButton, phoneNumber: button.phoneNumber || '' };
        default:
          return baseButton;
      }
    });

    const templateData = {
      name: formData.name,
      language: formData.language,
      category: formData.category.toUpperCase(),
      body: {
        text: formData.body.text,
        examples: formData.body.examples.filter(example => example.trim() !== '')
      },
      header: formData.header.text ? {
        type: 'TEXT',
        text: formData.header.text
      } : undefined,
      footer: formData.footer.text ? {
        text: formData.footer.text
      } : undefined,
      buttons: preparedButtons
    };

    return await axios.post(
      `${API_URL}/api/templates/${companyId}/create`,
      templateData,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Call POST API to create template
      const response = await createTemplate();
      
      if (response.status === 200 || response.status === 201) {
        alert("Template created successfully!");

        // Call sync API with the created template name
        await syncTemplates(formData.name);

        // Reset form
        setFormData({
          name: "",
          category: "",
          language: "en_US",
          header: {
            type: 'TEXT',
            text: ''
          },
          body: {
            text: "",
            examples: ['']
          },
          footer: {
            text: ""
          },
          buttons: [{ type: "QUICK_REPLY", text: "", payload: "" }],
        });

        // Close modal
        setShowCreateModal(false);

        // Refresh template list by fetching templates again
        await fetchTemplates();

      } else {
        throw new Error('Failed to create template');
      }

    } catch (err) {
      console.error("Error creating template:", err);
      // setError("Failed to create template. Please try again.");
    }
  };

  // Get category badge color
  const getCategoryColor = (category) => {
    switch (category) {
      case 'marketing': return 'bg-purple-100 text-purple-800';
      case 'utility': return 'bg-blue-100 text-blue-800';
      case 'authentication': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Extract variables from body text
  const extractVariables = (text) => {
    return text.match(/\{\{\d+\}\}/g) || [];
  };

  const variables = extractVariables(formData.body.text);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 md:mb-0">WhatsApp Template Manager</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-small py-2 px-4 rounded-md flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          Create Template
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Syncing and loading templates...</p>
        </div>
      )}

      {/* Templates Grid */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.length > 0 ? (
            templates.map(template => (
              <div key={template.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-gray-800 truncate">{template.name}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                      {template.status}
                    </span>
                  </div>
                  
                  <div className="mb-4 flex justify-between">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(template.category)}`}>
                      {template.category}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {template.language}
                    </span>
                  </div>

                  {template.header && (
                    <div className="mb-3 p-2 bg-gray-100 rounded">
                      <p className="text-sm text-gray-600">Header: {template.header}</p>
                    </div>
                  )}

                  <div className="mb-3 p-2 bg-gray-50 rounded">
                    <p className="text-sm text-gray-700 line-clamp-4">{template.body}</p>
                  </div>

                  {template.footer && (
                    <div className="mb-3 p-2 bg-gray-100 rounded">
                      <p className="text-sm text-gray-600">Footer: {template.footer}</p>
                    </div>
                  )}

                  {template.buttons.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-1">Buttons:</p>
                      <div className="flex flex-wrap gap-1">
                        {template.buttons.map((button, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {button.text}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={() => viewTemplateDetails(template)}
                      className="text-green-600 hover:text-green-800 text-sm font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-10">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating your first template.</p>
            </div>
          )}
        </div>
      )}

      {/* Template Detail Modal */}
      {showModal && selectedTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <h3 className="text-xl font-semibold text-gray-900">
                Template Details
              </h3>
              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                onClick={closeModal}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Name</p>
                  <p className="text-sm text-gray-900">{selectedTemplate.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Category</p>
                  <p className="text-sm text-gray-900">{selectedTemplate.category}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <p className="text-sm text-gray-900">{selectedTemplate.status}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Language</p>
                  <p className="text-sm text-gray-900">{selectedTemplate.language}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Active</p>
                  <p className="text-sm text-gray-900">{selectedTemplate.active ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {selectedTemplate.header && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Header</p>
                  <div className="mt-1 p-3 bg-gray-100 rounded-md">
                    <p className="text-sm text-gray-700">{selectedTemplate.header}</p>
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-500">Body</p>
                <div className="mt-1 p-3 bg-gray-100 rounded-md">
                  <p className="text-sm text-gray-700 whitespace-pre-line">{selectedTemplate.body}</p>
                </div>
              </div>

              {selectedTemplate.footer && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Footer</p>
                  <div className="mt-1 p-3 bg-gray-100 rounded-md">
                    <p className="text-sm text-gray-700">{selectedTemplate.footer}</p>
                  </div>
                </div>
              )}

              {selectedTemplate.buttons.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Buttons</p>
                  <div className="mt-2 space-y-2">
                    {selectedTemplate.buttons.map((button, index) => (
                      <div key={index} className="flex items-center p-2 bg-gray-100 rounded-md">
                        <span className="text-xs font-medium text-gray-700 mr-2">Type: {button.type}</span>
                        <span className="text-xs text-gray-700">Text: {button.text}</span>
                        {button.payload && (
                          <span className="text-xs text-gray-700 ml-2">Payload: {button.payload}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center p-6 space-x-2 border-t border-gray-200 rounded-b">
              <button
                onClick={closeModal}
                type="button"
                className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
       {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <h3 className="text-xl font-semibold text-gray-900">
                Create New Template
              </h3>
              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                onClick={() => setShowCreateModal(false)}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 
                    1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 
                    1.414L10 11.414l-4.293 4.293a1 1 
                    0 01-1.414-1.414L8.586 10 
                    4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                    focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    id="category"
                    name="category"
                    required
                    className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                    focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                    value={formData.category}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Category</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="UTILITY">Utility</option>
                    <option value="AUTHENTICATION">Authentication</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                  Language *
                </label>
                <select
                  id="language"
                  name="language"
                  required
                  className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                  focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                  value={formData.language}
                  onChange={handleInputChange}
                >
                  <option value="en_US">English (US)</option>
                  <option value="en_GB">English (UK)</option>
                  <option value="en">English</option>
                  <option value="es_ES">Spanish</option>
                  <option value="fr_FR">French</option>
                  <option value="de_DE">German</option>
                  <option value="it_IT">Italian</option>
                  <option value="pt_BR">Portuguese (Brazil)</option>
                  <option value="ru_RU">Russian</option>
                  <option value="ja_JP">Japanese</option>
                  <option value="ko_KR">Korean</option>
                  <option value="ar_AR">Arabic</option>
                  <option value="hi_IN">Hindi</option>
                </select>
              </div>

              <div>
                <label htmlFor="header" className="block text-sm font-medium text-gray-700 mb-1">
                  Header (Optional)
                </label>
                <input
                  type="text"
                  id="header"
                  className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                  focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                  value={formData.header.text}
                  onChange={(e) => handleHeaderChange(e.target.value)}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Body *
                  </label>
                  <button
                    type="button"
                    onClick={addVariableToBody}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    + Add Variable
                  </button>
                </div>
                <textarea
                  id="body"
                  required
                  rows={4}
                  className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                  focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                  value={formData.body.text}
                  onChange={(e) => handleBodyChange(e.target.value)}
                  placeholder="Use {{1}}, {{2}} for dynamic content. Click 'Add Variable' to insert variables."
                />
                
                {/* Variable examples input fields */}
                {variables.length > 0 && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Variable Examples (for testing):
                    </label>
                    {variables.map((variable, index) => (
                      <div key={index} className="mb-2">
                        <label className="block text-xs text-gray-600 mb-1">
                          Example for {variable}:
                        </label>
                        <input
                          type="text"
                          className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                          focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                          value={formData.body.examples[index] || ''}
                          onChange={(e) => handleExampleChange(index, e.target.value)}
                          placeholder={`Enter example value for ${variable}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="footer" className="block text-sm font-medium text-gray-700 mb-1">
                  Footer (Optional)
                </label>
                <input
                  type="text"
                  id="footer"
                  className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                  focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                  value={formData.footer.text}
                  onChange={(e) => handleFooterChange(e.target.value)}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Buttons (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={addButton}
                    className="text-xs text-green-600 hover:text-green-800 font-medium"
                  >
                    + Add Button
                  </button>
                </div>

                {formData.buttons.map((button, index) => (
                  <div key={index} className="space-y-2 mb-4 p-3 border rounded-md">
                    <div className="flex items-center space-x-2">
                      <select
                        className="w-1/2 rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                        focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                        value={button.type}
                        onChange={(e) => handleButtonChange(index, 'type', e.target.value)}
                      >
                        <option value="QUICK_REPLY">Quick Reply</option>
                        <option value="URL">URL</option>
                        <option value="PHONE_NUMBER">Phone Number</option>
                      </select>
                      {formData.buttons.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeButton(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 
                              01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 
                              0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      placeholder="Button text"
                      className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                      focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                      value={button.text}
                      onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                    />
                    {button.type === 'QUICK_REPLY' && (
                      <input
                        type="text"
                        placeholder="Payload (for quick replies)"
                        className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                        focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                        value={button.payload}
                        onChange={(e) => handleButtonChange(index, 'payload', e.target.value)}
                      />
                    )}
                    {button.type === 'URL' && (
                      <input
                        type="text"
                        placeholder="URL"
                        className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                        focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                        value={button.url}
                        onChange={(e) => handleButtonChange(index, 'url', e.target.value)}
                      />
                    )}
                    {button.type === 'PHONE_NUMBER' && (
                      <input
                        type="text"
                        placeholder="Phone Number"
                        className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm 
                        focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm"
                        value={button.phoneNumber}
                        onChange={(e) => handleButtonChange(index, 'phone_number', e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center p-6 space-x-2 border-t border-gray-200 rounded-b">
                <button
                  type="submit"
                  className="text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none 
                  focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                >
                  Create Template
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none 
                  focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 
                  hover:text-gray-900 focus:z-10"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default CreateWhatsappTemplate;